import React, { useCallback, useEffect, useMemo, useState } from "react";
import { message, Modal } from "antd";
import AdminMobileShell from "./AdminMobileShell";
import {
  Summary, SearchBar, EmptyState, Fab, Sheet, ActionRow, DetailRow,
  Field, TextInput, Pill, Mono, Note, Ico, fmt,
} from "../produits/productMobileKit";
import { useSelection } from "../../SelectionContext";
import { useAuth } from "../../context/AuthContext";
import {
  deleteRole, duplicateRole, getRolePermissions, listPermissions, listRoles,
  saveRole, saveRolePermissions,
} from "./adminMobileApi";
import { getPermissionListLabel } from "../../utils/permissionPresentation";

const errorMessage = (error, fallback) => error?.response?.data?.error || error?.response?.data?.message || error?.message || fallback;

export default function RolesPermissionsMobile() {
  const { selectedCompany } = useSelection();
  const { user, hasPermission, refreshUserPermissions } = useAuth();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [detail, setDetail] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [duplicating, setDuplicating] = useState(null);
  const [roleName, setRoleName] = useState("");
  const [savingRole, setSavingRole] = useState(false);
  const [permissionOpen, setPermissionOpen] = useState(false);
  const [permissionRole, setPermissionRole] = useState(null);
  const [checked, setChecked] = useState([]);
  const [permissionQuery, setPermissionQuery] = useState("");
  const [permissionLoading, setPermissionLoading] = useState(false);
  const [permissionSaving, setPermissionSaving] = useState(false);

  const isSuperadmin =
    user?.is_superadmin === true || Number(user?.is_superadmin) === 1;
  const canView = isSuperadmin || hasPermission("Admin.RolesPermissions.view");
  const canCreate = isSuperadmin || hasPermission("Admin.RolesPermissions.create");
  const canEdit = isSuperadmin || hasPermission("Admin.RolesPermissions.edit");
  const canDelete = isSuperadmin || hasPermission("Admin.RolesPermissions.delete");
  const canAssignPermissions =
    isSuperadmin ||
    hasPermission("Admin.RolesPermissions.assign_permissions");
  const activeCompanyId = Number(selectedCompany || user?.company_id) || null;
  const canMutateRole = (role) =>
    Boolean(
      role &&
        (isSuperadmin ||
          (role.company_id &&
            activeCompanyId &&
            Number(role.company_id) === activeCompanyId))
    );

  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const [roleRows, permissionRows] = await Promise.all([listRoles(selectedCompany), listPermissions()]);
      setRoles(roleRows);
      setPermissions(permissionRows);
    } catch (error) { message.error(errorMessage(error, "Impossible de charger les rôles et permissions")); }
    finally { setLoading(false); }
  }, [canView, selectedCompany]);

  useEffect(() => { load(); }, [load]);

  const visibleRoles = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return !needle ? roles : roles.filter((role) => role.name.toLowerCase().includes(needle));
  }, [query, roles]);

  const groupedPermissions = useMemo(() => {
    const needle = permissionQuery.trim().toLowerCase();
    const groups = {};
    permissions.forEach((permission) => {
      if (needle && !`${permission} ${getPermissionListLabel(permission)}`.toLowerCase().includes(needle)) return;
      const group = permission.split(".")[0] || "Autres";
      if (!groups[group]) groups[group] = [];
      groups[group].push(permission);
    });
    return Object.entries(groups).sort(([left], [right]) => left.localeCompare(right, "fr"));
  }, [permissionQuery, permissions]);

  const openForm = (role = null) => {
    setDetail(null);
    setDuplicating(null);
    setEditing(role);
    setRoleName(role?.name || "");
    setFormOpen(true);
  };

  const openDuplicateForm = (role) => {
    const targetCompanyId = selectedCompany ? Number(selectedCompany) : null;
    const existingNames = new Set(
      roles
        .filter((candidate) => (candidate.company_id ? Number(candidate.company_id) : null) === targetCompanyId)
        .map((candidate) => candidate.name.toLocaleLowerCase("fr"))
    );
    let suggestedName = `${role.name} (copie)`;
    let copyNumber = 2;
    while (existingNames.has(suggestedName.toLocaleLowerCase("fr"))) {
      suggestedName = `${role.name} (copie ${copyNumber})`;
      copyNumber += 1;
    }

    setDetail(null);
    setEditing(null);
    setDuplicating(role);
    setRoleName(suggestedName);
    setFormOpen(true);
  };

  const submitRole = async () => {
    if (!roleName.trim()) return;
    const allowed = duplicating
      ? canCreate && canAssignPermissions && canMutateRole(duplicating)
      : editing
        ? canEdit && canMutateRole(editing)
        : canCreate && (isSuperadmin || Boolean(activeCompanyId));
    if (!allowed) {
      message.error("Vous n'avez pas la permission de modifier ce rôle");
      return;
    }
    setSavingRole(true);
    try {
      if (duplicating) {
        const duplicatedRole = await duplicateRole(duplicating.id, roleName.trim(), selectedCompany);
        const permissionsCount = duplicatedRole.permissions_count || 0;
        message.success(`Rôle dupliqué avec ${permissionsCount} permission${permissionsCount === 1 ? "" : "s"}`);
      } else {
        await saveRole(editing?.id, roleName.trim(), editing ? editing.company_id : selectedCompany);
        message.success(editing ? "Rôle mis à jour" : "Rôle créé");
      }
      setFormOpen(false);
      setEditing(null);
      setDuplicating(null);
      await load();
    } catch (error) { message.error(errorMessage(error, "Impossible d'enregistrer le rôle")); }
    finally { setSavingRole(false); }
  };

  const remove = (role) => Modal.confirm({
    title: "Supprimer ce rôle ?", content: role.name, okText: "Supprimer", okType: "danger", cancelText: "Annuler", centered: true,
    onOk: async () => {
      try { await deleteRole(role.id, role.company_id); message.success("Rôle supprimé"); setDetail(null); await load(); }
      catch (error) { message.error(errorMessage(error, "Suppression impossible")); }
    },
  });

  const openPermissions = async (role) => {
    setDetail(null);
    setPermissionRole(role);
    setPermissionOpen(true);
    setPermissionQuery("");
    setPermissionLoading(true);
    try { setChecked(await getRolePermissions(role.id)); }
    catch (error) { message.error(errorMessage(error, "Impossible de charger les permissions du rôle")); }
    finally { setPermissionLoading(false); }
  };

  const togglePermission = (permission) => setChecked((previous) => previous.includes(permission) ? previous.filter((item) => item !== permission) : [...previous, permission]);
  const toggleGroup = (groupPermissions) => setChecked((previous) => {
    const allChecked = groupPermissions.every((permission) => previous.includes(permission));
    return allChecked ? previous.filter((permission) => !groupPermissions.includes(permission)) : [...new Set([...previous, ...groupPermissions])];
  });

  const submitPermissions = async () => {
    if (
      !permissionRole ||
      permissionRole.name === "SysAdmin" ||
      !canAssignPermissions ||
      !canMutateRole(permissionRole)
    ) {
      message.error("Vous n'avez pas la permission d'attribuer ces droits");
      return;
    }
    setPermissionSaving(true);
    try {
      await saveRolePermissions(permissionRole.id, checked);
      await refreshUserPermissions(true);
      message.success("Permissions enregistrées");
      setPermissionOpen(false);
    } catch (error) { message.error(errorMessage(error, "Impossible d'enregistrer les permissions")); }
    finally { setPermissionSaving(false); }
  };

  if (!canView) return <AdminMobileShell title="Rôles et permissions"><div className="body"><EmptyState icon="alert-circle" title="Accès non autorisé" /></div></AdminMobileShell>;

  const companyRoles = roles.filter((role) => role.company_id).length;
  return (
    <AdminMobileShell title="Rôles et permissions" subtitle={`${fmt(roles.length)} rôle${roles.length > 1 ? "s" : ""}`}>
      <div className="appbar" style={{ borderTop: 0, paddingTop: 0 }}>
        <Summary items={[{ label: "Rôles", value: fmt(roles.length) }, { label: "Entreprise", value: fmt(companyRoles) }, { label: "Permissions", value: fmt(permissions.length) }]} />
        <SearchBar value={query} onChange={setQuery} placeholder="Rechercher un rôle…" />
      </div>
      <div className="body">
        {loading && !roles.length ? <EmptyState loading title="Chargement…" /> : !visibleRoles.length ? <EmptyState icon="shield" title="Aucun rôle" /> : (
          <div className="list">
            {visibleRoles.map((role) => <button key={role.id} className="rcard" onClick={() => setDetail(role)}>
              <Mono name={role.name} square />
              <div className="rcard__main"><div className="rcard__name">{role.name}</div><div className="rcard__meta"><Pill tone={role.company_id ? "info" : "ok"} label={role.company_id ? "Entreprise" : "Global"} /><span className="rcard__sub">#{role.id}</span></div></div>
              <span className="rcard__chev"><Ico name="chevron-right" /></span>
            </button>)}
          </div>
        )}
      </div>
      {canCreate && (isSuperadmin || selectedCompany) ? <Fab label="Nouveau rôle" onClick={() => openForm()} /> : null}

      <Sheet open={Boolean(detail)} onClose={() => setDetail(null)} title={detail?.name}>
        {detail ? <>
          <DetailRow label="Identifiant" value={`#${detail.id}`} /><DetailRow label="Portée" value={detail.company_id ? "Entreprise" : "Globale"} />
          <ActionRow icon="shield-check" label={detail.name === "SysAdmin" ? "Accès complet" : canAssignPermissions && canMutateRole(detail) ? "Configurer les permissions" : "Consulter les permissions"} onClick={() => openPermissions(detail)} />
          {canCreate && canAssignPermissions && canMutateRole(detail) ? <ActionRow icon="copy" label="Dupliquer avec les permissions" onClick={() => openDuplicateForm(detail)} /> : null}
          {canEdit && canMutateRole(detail) && detail.name !== "SysAdmin" ? <ActionRow icon="pencil" label="Renommer" onClick={() => openForm(detail)} /> : null}
          {canDelete && canMutateRole(detail) && detail.name !== "SysAdmin" ? <ActionRow icon="trash-2" label="Supprimer" danger onClick={() => remove(detail)} /> : null}
        </> : null}
      </Sheet>

      <Sheet open={formOpen} onClose={() => setFormOpen(false)} title={duplicating ? "Dupliquer le rôle" : editing ? "Renommer le rôle" : "Nouveau rôle"}>
        <Field label="Nom du rôle *"><TextInput value={roleName} onChange={setRoleName} autoFocus /></Field>
        {duplicating ? <Note>Toutes les permissions actuelles de « {duplicating.name} » seront copiées. {selectedCompany ? "Le nouveau rôle sera lié à l'entreprise sélectionnée." : "Le nouveau rôle aura une portée globale."}</Note> : !editing ? <Note>{selectedCompany ? "Le rôle sera créé pour l'entreprise actuellement sélectionnée." : "Aucune entreprise sélectionnée : le rôle sera créé avec une portée globale."}</Note> : null}
        <div className="sheet__actions"><button className="btn btn--ghost" onClick={() => setFormOpen(false)}>Annuler</button><button className="btn btn--primary" onClick={submitRole} disabled={savingRole || !roleName.trim()}>{savingRole ? "Enregistrement…" : duplicating ? "Dupliquer" : editing ? "Mettre à jour" : "Créer"}</button></div>
      </Sheet>

      <Sheet open={permissionOpen} onClose={() => setPermissionOpen(false)} title="Permissions" subtitle={permissionRole?.name}>
        {permissionRole?.name === "SysAdmin" ? <Note>Le rôle SysAdmin dispose automatiquement d'un accès complet. Aucune configuration n'est nécessaire.</Note> : <>
          <SearchBar value={permissionQuery} onChange={setPermissionQuery} placeholder="Rechercher une permission…" />
          {permissionLoading ? <EmptyState loading title="Chargement…" /> : groupedPermissions.map(([group, groupPermissions]) => {
            const allChecked = groupPermissions.every((permission) => checked.includes(permission));
            return <div key={group} style={{ marginTop: 16 }}>
              <button type="button" className="actsheet__row" disabled={!canAssignPermissions || !canMutateRole(permissionRole)} onClick={() => toggleGroup(groupPermissions)}><Ico name={allChecked ? "check-circle-2" : "circle"} /><strong>{group}</strong><span style={{ marginLeft: "auto" }}>{groupPermissions.filter((permission) => checked.includes(permission)).length}/{groupPermissions.length}</span></button>
              {groupPermissions.map((permission) => <button key={permission} type="button" className="actsheet__row" disabled={!canAssignPermissions || !canMutateRole(permissionRole)} onClick={() => togglePermission(permission)}><Ico name={checked.includes(permission) ? "check-circle-2" : "circle"} /><span><span style={{ display: "block" }}>{getPermissionListLabel(permission)}</span><small>{permission}</small></span></button>)}
            </div>;
          })}
          <div className="sheet__actions"><button className="btn btn--ghost" onClick={() => setPermissionOpen(false)}>Fermer</button>{canAssignPermissions && canMutateRole(permissionRole) ? <button className="btn btn--primary" onClick={submitPermissions} disabled={permissionSaving || permissionLoading}>{permissionSaving ? "Enregistrement…" : "Enregistrer"}</button> : null}</div>
        </>}
      </Sheet>
    </AdminMobileShell>
  );
}
